import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Building2,
  CheckCircle2,
  FolderKanban,
  Loader2,
  Package,
  Target,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useMeetingList } from "@/modules/meetings/hooks/useMeetings";
import { useTaskList } from "@/modules/tasks/hooks/useTasks";
import { useDecisionList } from "@/modules/decisions/hooks/useDecisions";
import { useMilestoneList } from "@/modules/milestones/hooks/useMilestones";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { useCreateAction } from "../hooks/useActions";
import type { ActionPriority, ActionSource, ActionStatus } from "../types";

type LockedFields = {
  companyId?: boolean;
  productId?: boolean;
  projectId?: boolean;
  meetingId?: boolean;
  taskId?: boolean;
  decisionId?: boolean;
  milestoneId?: boolean;
};

type PrefillContext = {
  companyId?: string | null;
  productId?: string | null;
  projectId?: string | null;
  meetingId?: string | null;
  meetingTitle?: string | null;
  taskId?: string | null;
  taskTitle?: string | null;
  taskCode?: string | null;
  decisionId?: string | null;
  decisionTitle?: string | null;
  milestoneId?: string | null;
  milestoneTitle?: string | null;
  locked?: LockedFields;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
  prefill?: PrefillContext;
};

export function NewActionDrawer({
  isOpen,
  onClose,
  onCreated,
  prefill,
}: Props) {
  const { user } = useAuth();
  const createAction = useCreateAction();

  const lockTask = !!prefill?.locked?.taskId && !!prefill?.taskId;
  const lockMeeting = !!prefill?.locked?.meetingId && !!prefill?.meetingId;
  const lockProject = !!prefill?.locked?.projectId && !!prefill?.projectId;
  const lockCompany = !!prefill?.locked?.companyId && !!prefill?.companyId;
  const lockProduct = !!prefill?.locked?.productId && !!prefill?.productId;
  const lockDecision =
    !!prefill?.locked?.decisionId && !!prefill?.decisionId;
  const lockMilestone =
    !!prefill?.locked?.milestoneId && !!prefill?.milestoneId;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<ActionPriority>("MEDIUM");
  const [status, setStatus] = useState<ActionStatus>("OPEN");
  const [source, setSource] = useState<ActionSource>(
    prefill?.taskId ? "TASK" : prefill?.meetingId ? "MEETING" : "MANUAL",
  );
  const [dueDate, setDueDate] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(
    prefill?.companyId ?? "",
  );
  const [selectedProductId, setSelectedProductId] = useState(
    prefill?.productId ?? "",
  );
  const [selectedProjectId, setSelectedProjectId] = useState(
    prefill?.projectId ?? "",
  );
  const [selectedMeetingId, setSelectedMeetingId] = useState(
    prefill?.meetingId ?? "",
  );
  const [selectedTaskId, setSelectedTaskId] = useState(prefill?.taskId ?? "");
  const [selectedDecisionId, setSelectedDecisionId] = useState(
    prefill?.decisionId ?? "",
  );
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(
    prefill?.milestoneId ?? "",
  );
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [assignedToId, setAssignedToId] = useState(user?.id ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedCompanyId(prefill?.companyId ?? "");
      setSelectedProductId(prefill?.productId ?? "");
      setSelectedProjectId(prefill?.projectId ?? "");
      setSelectedMeetingId(prefill?.meetingId ?? "");
      setSelectedTaskId(prefill?.taskId ?? "");
      setSelectedDecisionId(prefill?.decisionId ?? "");
      setSelectedMilestoneId(prefill?.milestoneId ?? "");
      setSource(
        prefill?.taskId ? "TASK" : prefill?.meetingId ? "MEETING" : "MANUAL",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isOpen,
    prefill?.companyId,
    prefill?.productId,
    prefill?.projectId,
    prefill?.meetingId,
    prefill?.taskId,
  ]);

  useEffect(() => {
    if (user?.id && !assignedToId) setAssignedToId(user.id);
  }, [user?.id, assignedToId]);

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 100,
    operationalOnly: true,
  });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
    productId: selectedProductId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: meetingsResult } = useMeetingList({
    page: 1,
    limit: 100,
    projectId: selectedProjectId || undefined,
  });
  const meetings = meetingsResult?.data ?? [];

  const { data: tasksResult } = useTaskList({
    page: 1,
    limit: 100,
    projectId: selectedProjectId || undefined,
  });
  const tasks = tasksResult?.data ?? [];

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  const { data: decisionsResult } = useDecisionList({ page: 1, limit: 100 });
  const allDecisions = decisionsResult?.data ?? [];
  const { data: milestonesResult } = useMilestoneList({ page: 1, limit: 100 });
  const allMilestones = milestonesResult?.data ?? [];

  function reset() {
    setTitle("");
    setDescription("");
    setPriority("MEDIUM");
    setStatus("OPEN");
    setSource(
      prefill?.taskId ? "TASK" : prefill?.meetingId ? "MEETING" : "MANUAL",
    );
    setDueDate("");
    setSelectedCompanyId(prefill?.companyId ?? "");
    setSelectedProductId(prefill?.productId ?? "");
    setSelectedProjectId(prefill?.projectId ?? "");
    setSelectedMeetingId(prefill?.meetingId ?? "");
    setSelectedTaskId(prefill?.taskId ?? "");
    setSelectedDecisionId(prefill?.decisionId ?? "");
    setSelectedMilestoneId(prefill?.milestoneId ?? "");
    setSelectedPhaseId(null);
    setSelectedStageId(null);
    setAssignedToId(user?.id ?? "");
    setError(null);
  }

  const showPhasePicker =
    !!selectedProjectId && !prefill?.taskId && !prefill?.meetingId;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    setError(null);
    try {
      const res = await createAction.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        status,
        priority,
        source,
        dueDate: dueDate ? new Date(dueDate).toISOString() : null,
        companyId: selectedCompanyId || null,
        productId: selectedProductId || null,
        projectId: selectedProjectId || null,
        phaseId: showPhasePicker ? selectedPhaseId : null,
        stageId: showPhasePicker ? selectedStageId : null,
        meetingId: selectedMeetingId || null,
        taskId: selectedTaskId || null,
        decisionId: selectedDecisionId || null,
        milestoneId: selectedMilestoneId || null,
        assignedToId: assignedToId || null,
      });
      onCreated?.(res.action.code);
      reset();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create action");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] bg-black/30"
        onClick={onClose}
      />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">New Action</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-6 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Action Details
            </h3>

            <div className="space-y-2">
              <Label htmlFor="act-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="act-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter action title"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="act-desc">Description</Label>
              <textarea
                id="act-desc"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the action (optional)"
                className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Priority &amp; Status
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="act-priority">Priority</Label>
                <Select
                  id="act-priority"
                  value={priority}
                  onChange={(e) =>
                    setPriority(e.target.value as ActionPriority)
                  }
                >
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="act-status">Status</Label>
                <Select
                  id="act-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ActionStatus)}
                >
                  <option value="OPEN">Open</option>
                  <option value="IN_PROGRESS">In Progress</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="act-source">Source</Label>
                <Select
                  id="act-source"
                  value={source}
                  disabled={lockTask || lockMeeting}
                  onChange={(e) => setSource(e.target.value as ActionSource)}
                >
                  <option value="MANUAL">Manual</option>
                  <option value="MEETING">Meeting</option>
                  <option value="TASK">Task</option>
                </Select>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">Dates</h3>
            <div className="space-y-2">
              <Label htmlFor="act-due">Due Date</Label>
              <Input
                id="act-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Association
            </h3>

            <div className="space-y-2">
              <Label htmlFor="act-company">Company</Label>
              <div className="relative">
                <Building2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="act-company"
                  value={selectedCompanyId}
                  disabled={lockCompany || lockTask || lockMeeting || lockProject}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value);
                    setSelectedProductId("");
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">Select a company...</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="act-product">Product</Label>
              <div className="relative">
                <Package
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="act-product"
                  value={selectedProductId}
                  disabled={lockProduct || lockTask || lockMeeting || lockProject}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value);
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="act-project">Project</Label>
              <div className="relative">
                <FolderKanban
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="act-project"
                  value={selectedProjectId}
                  disabled={lockProject || lockTask || lockMeeting}
                  onChange={(e) => {
                    setSelectedProjectId(e.target.value);
                    setSelectedPhaseId(null);
                    setSelectedStageId(null);
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {showPhasePicker && (
              <PhaseStagePicker
                projectId={selectedProjectId || null}
                phaseId={selectedPhaseId}
                stageId={selectedStageId}
                onPhaseChange={setSelectedPhaseId}
                onStageChange={setSelectedStageId}
              />
            )}

            {/* Meeting */}
            {lockMeeting && prefill?.meetingTitle ? (
              <div className="space-y-2">
                <Label>Meeting</Label>
                <div
                  className="rounded-md border px-3 py-2 text-sm text-muted-foreground"
                  style={{
                    borderColor: "hsl(210,30%,85%)",
                    background: "hsl(210,16%,97%)",
                  }}
                >
                  {prefill.meetingTitle}
                </div>
              </div>
            ) : !lockTask ? (
              <div className="space-y-2">
                <Label htmlFor="act-meeting">Meeting</Label>
                <select
                  id="act-meeting"
                  value={selectedMeetingId}
                  onChange={(e) => setSelectedMeetingId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No meeting</option>
                  {meetings.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.title}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {/* Task */}
            {lockTask && (prefill?.taskCode || prefill?.taskTitle) ? (
              <div className="space-y-2">
                <Label>Task</Label>
                <div
                  className="rounded-md border px-3 py-2 text-sm text-muted-foreground"
                  style={{
                    borderColor: "hsl(210,30%,85%)",
                    background: "hsl(210,16%,97%)",
                  }}
                >
                  <span className="font-mono">{prefill.taskCode}</span>
                  {prefill.taskTitle ? ` — ${prefill.taskTitle}` : ""}
                </div>
              </div>
            ) : !lockMeeting ? (
              <div className="space-y-2">
                <Label htmlFor="act-task">Task</Label>
                <select
                  id="act-task"
                  value={selectedTaskId}
                  onChange={(e) => setSelectedTaskId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No task</option>
                  {tasks.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.code} — {t.title}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="act-decision">Decision</Label>
              <div className="relative">
                <CheckCircle2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="act-decision"
                  value={selectedDecisionId}
                  disabled={lockDecision}
                  onChange={(e) => setSelectedDecisionId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No decision</option>
                  {allDecisions.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.code} — {d.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="act-milestone">Milestone</Label>
              <div className="relative">
                <Target
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="act-milestone"
                  value={selectedMilestoneId}
                  disabled={lockMilestone}
                  onChange={(e) => setSelectedMilestoneId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors disabled:bg-muted disabled:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No milestone</option>
                  {allMilestones.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.code} — {m.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <h3 className="text-sm font-semibold text-foreground">
              Assignment
            </h3>
            <div className="space-y-2">
              <Label htmlFor="act-assignee">Assigned To</Label>
              <select
                id="act-assignee"
                value={assignedToId}
                onChange={(e) => setAssignedToId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <option value="">Unassigned</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                style={{
                  borderColor: "hsl(0,70%,80%)",
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,35%)",
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!title.trim() || createAction.isPending}
            >
              {createAction.isPending && (
                <Loader2
                  className="mr-1 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
              )}
              Create Action
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
